import React, { useCallback, useMemo, useState } from "react";
import type { ChoiceItem, Question } from "../types/question.types";

export interface MultipleChoiceProps {
  question: Extract<Question, { layout: "multiple_choice" }>;
  value?: Record<string, number>;
  onChange?: (payload: Record<string, number>) => void;
}

const getStoredValue = (
  value: Record<string, number> | undefined,
  key: string,
): number | undefined => {
  const v = value?.[key];
  if (v === undefined) return undefined;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const buildPayload = (
  choices: ChoiceItem[],
  selectedKeys: Set<string>,
): Record<string, number> => {
  const payload: Record<string, number> = {};

  for (const choice of choices) {
    const key = choice.attributes.answer_key;
    if (!key) continue;

    payload[key] = selectedKeys.has(choice.key)
      ? Number(choice.attributes.selected_value)
      : Number(choice.attributes.unselected_value);
  }

  return payload;
};

const MultipleChoice: React.FC<MultipleChoiceProps> = ({
  question,
  value,
  onChange,
}) => {
  const { title, choices, allowed_choices } = question.attributes;

  const [localSelectedKeys, setLocalSelectedKeys] = useState<Set<string>>(
    () => new Set(),
  );

  const isControlled = value != null;

  const selectedKeys = useMemo(() => {
    if (!isControlled || !choices?.length) return new Set<string>();
    return new Set(
      choices
        .filter((c) => {
          const stored = getStoredValue(value, c.attributes.answer_key);
          return stored === Number(c.attributes.selected_value);
        })
        .map((c) => c.key),
    );
  }, [isControlled, choices, value]);

  const displaySelectedKeys = isControlled ? selectedKeys : localSelectedKeys;

  const handleCheckboxChange = useCallback(
    (key: string, checked: boolean) => {
      const choice = choices.find((c) => c.key === key);
      console.log("choice", choice);
      if (!choice) return;

      const getNextSelectedKeys = (current: Set<string>): Set<string> => {
        const next = new Set(current);
        if (checked) {
          next.add(key);
          if (allowed_choices > 0 && next.size > allowed_choices) {
            const arr = Array.from(next);
            arr.shift();
            return new Set(arr);
          }
          return next;
        }
        next.delete(key);

        return next;
      };

      if (isControlled) {
        const currentKeys = new Set(
          choices
            .filter((c) => {
              const stored = getStoredValue(value, c.attributes.answer_key);
              return stored === Number(c.attributes.selected_value);
            })
            .map((c) => c.key),
        );
        const nextKeys = getNextSelectedKeys(currentKeys);
        const payload = buildPayload(choices, nextKeys);
        onChange?.(payload);
        return;
      }

      setLocalSelectedKeys((prev) => {
        const nextKeys = getNextSelectedKeys(prev);
        const payload = buildPayload(choices, nextKeys);
        onChange?.(payload);
        return nextKeys;
      });
    },
    [allowed_choices, choices, onChange, isControlled, value],
  );

  if (!choices?.length) return null;

  return (
    <div className="flex flex-col py-8 min-h-0">
      <h3
        className="text-[#26295e] font-primary font-semibold text-lg mb-4"
        style={{ fontFamily: "var(--font-primary)" }}
      >
        {title}
      </h3>
      <fieldset className="border-0 p-0 m-0 flex flex-col gap-3">
        <legend className="sr-only">{title}</legend>
        {choices.map((c) => {
          const key = c.key;
          const label = c.attributes.title;
          const checked = displaySelectedKeys.has(key);
          return (
            <label
              key={key}
              className="flex items-center gap-3 cursor-pointer text-[#374151] font-primary hover:text-[#26295e]"
            >
              <input
                type="checkbox"
                value={key}
                checked={checked}
                onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-[#26295e] focus:ring-[#26295e]"
              />
              <span>{label}</span>
            </label>
          );
        })}
      </fieldset>
    </div>
  );
};

export default MultipleChoice;
